/**
 * 分页纯函数工具。独立成文件以保证 `pagination.tsx` 只导出 React 组件，
 * 避免 Vite React Fast Refresh 因「组件与非组件混合导出」而失效（
 * 表现为 HMR 日志 `Could not Fast Refresh ("getPageRange" export is incompatible)`）。
 */

export type PageRangeItem = number | "ellipsis";

/** {@link getPageRange} 的可选配置（对齐常见 UI 库的 boundary / sibling 语义） */
export type GetPageRangeOptions = {
  /** 首部与尾部各固定展示的页数，默认 1（对齐 Ant Design 默认） */
  boundaryCount?: number;
  /** 当前页左右各展示的相邻页数，默认 2（对齐 Ant Design 默认） */
  siblingCount?: number;
};

/**
 * 根据当前页与总页数计算需要展示的页码数组（含 `'ellipsis'`）。
 *
 * 策略（对齐 Ant Design 默认）：合并「首部若干页」「尾部若干页」「当前页邻域」，
 * 排序后若相邻页码间隔大于 1 则插入省略号。总页数不超过紧凑阈值时直接展示全部页码、不出现省略号。
 *
 * 默认 `boundaryCount = 1`、`siblingCount = 2`，紧凑阈值为 `boundaryCount * 2 + siblingCount * 2 + 1`（即 7）。
 *
 * @example
 * getPageRange(1, 5) // [1, 2, 3, 4, 5]
 * getPageRange(50, 100) // [1, 'ellipsis', 48, 49, 50, 51, 52, 'ellipsis', 100]
 * getPageRange(3, 20) // [1, 2, 3, 4, 5, 'ellipsis', 20]
 */
export function getPageRange(
  current: number,
  total: number,
  options?: GetPageRangeOptions,
): PageRangeItem[] {
  const boundaryCount = Math.max(1, options?.boundaryCount ?? 1);
  const siblingCount = Math.max(0, options?.siblingCount ?? 2);

  if (total <= 1) return [1];

  const compactMax = boundaryCount * 2 + siblingCount * 2 + 1;
  if (total <= compactMax) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const currentClamped = Math.min(Math.max(current, 1), total);
  const pages = new Set<number>();

  for (let p = 1; p <= boundaryCount; p += 1) {
    if (p <= total) pages.add(p);
  }
  for (let p = total - boundaryCount + 1; p <= total; p += 1) {
    if (p >= 1) pages.add(p);
  }

  const sStart = Math.max(1, currentClamped - siblingCount);
  const sEnd = Math.min(total, currentClamped + siblingCount);
  for (let p = sStart; p <= sEnd; p += 1) pages.add(p);

  pages.add(1);
  pages.add(total);

  const sorted = [...pages].sort((a, b) => a - b);
  const out: PageRangeItem[] = [];

  for (let i = 0; i < sorted.length; i += 1) {
    const p = sorted[i];
    if (i === 0) {
      out.push(p);
      continue;
    }
    const prev = sorted[i - 1];
    if (p - prev > 1) out.push("ellipsis");
    out.push(p);
  }

  return out;
}
